import * as msal from "@azure/msal-browser";
import store from './index';
import axios from 'axios';
import { computed } from "vue";


let refreshPromise = null;
// Prevents stacking multiple interactive loginRedirect calls.
let loginInProgress = false;
let globalGuardInstalled = false;

// Resolves once authentication has fully completed (session cookie set, axios
// prepared). Route components mount in parallel with init(), so any data fetch
// that runs on mount must await this — otherwise it races the login handshake,
// gets a silent 401, and the page sits on an empty list (the classic
// "opened the app from an email link and nothing loads" bug).
let authReadyResolve;
const authReadyPromise = new Promise((resolve) => { authReadyResolve = resolve; });
function markAuthReady() {
  if (authReadyResolve) {
    authReadyResolve();
    authReadyResolve = null;
  }
  // App.vue renders the router-view only once this flips — no page (or its
  // on-mount data fetching) exists before login has fully completed.
  store.commit('authReady', true);
}

// Deep links (e.g. the "Review in Admin App" email button) must survive the
// interactive AAD redirect, whose registered redirectUri is the origin root.
const POST_LOGIN_ROUTE_KEY = 'dsg_post_login_route';

const APP_VERSION_KEY = 'dsg_admin_app_version';

// On a new app version: drop the signed-in session and every cache so the user
// gets a clean login against the fresh build (the service worker otherwise
// keeps serving stale bundles and the old MSAL/backend session sticks around).
async function enforceVersionFreshness() {
  try {
    const currentVersion = process.env.VUE_APP_VERSION;
    if (!currentVersion) return;
    const lastVersion = localStorage.getItem(APP_VERSION_KEY);
    if (lastVersion === currentVersion) return;

    if (lastVersion) {
      console.log(`App updated ${lastVersion} -> ${currentVersion}: clearing caches and forcing re-login`);
      sessionStorage.clear(); // MSAL account/token cache
      localStorage.removeItem('auth');
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }
      if (navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    }
    localStorage.setItem(APP_VERSION_KEY, currentVersion);
  } catch (error) {
    console.error('Version freshness check failed', error);
  }
}

function isApiRequest(config) {
  const url = config?.url || '';
  if (url.startsWith('/api') || url.startsWith(`${location.origin}/api`)) return true;
  return Boolean(config?.baseURL && config.baseURL.includes('/api'));
}


function installAuthGuard(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const status = error?.response?.status;
      const url = config?.url || '';
      if (
        status !== 401 ||
        !config ||
        config._authRetry ||
        !isApiRequest(config) ||
        url.includes('/api/set_cookie') ||
        url.includes('/api/init')
      ) {
        return Promise.reject(error);
      }
      const refreshed = await authModule.refreshSession();
      if (!refreshed) {
        await authModule.login();
        return Promise.reject(error);
      }
      config._authRetry = true;
      return instance.request(config);
    }
  );
}

const authModule = {
  // Await this before any on-mount data fetch. Never resolves if the user is
  // being redirected to the sign-in page (the navigation aborts everything).
  ready() {
    return authReadyPromise;
  },

  async init() {
    // Runs before MSAL is set up: with the session cache cleared, the flow
    // below finds no account and redirects to a fresh sign-in.
    await enforceVersionFreshness();
    // Install the 401-retry guard on the global axios up front — components
    // fire global-axios requests while init is still running.
    if (!globalGuardInstalled) {
      installAuthGuard(axios);
      globalGuardInstalled = true;
    }
    try {
      // The /health session probe doesn't depend on /api/init, so run both in parallel.
      const [initRes, healthRes] = await Promise.allSettled([
        axios.get(`/api/init`, { withCredentials: true }),
        axios.get('/health', { withCredentials: true }),
      ]);
      if (initRes.status === 'rejected') throw initRes.reason;
      const logindata = initRes.value.data;
      store.commit("logindata", logindata);

      const msalConfig = {
        auth: {
          clientId: logindata.MS_APP_ID,
          authority: `https://login.microsoftonline.com/${logindata.TENANT_ID}`,
        },
      };

      const msalApp = new msal.PublicClientApplication(msalConfig);
      const accounts = msalApp.getAllAccounts();
      if (accounts.length > 0) {
        msalApp.setActiveAccount(accounts[0]);
        store.commit("activeAccount", accounts[0]);
      }
      store.commit("msalApp", msalApp);

      if (accounts.length > 0) {
        const health401 = healthRes.status === 'rejected' && healthRes.reason?.response?.status === 401;
        if (health401) {
          // Backend cookie expired but MSAL still has the account: renew the
          // session silently instead of bouncing through the sign-in page.
          const refreshed = await this.refreshSession();
          if (!refreshed) {
            await this.login(msalApp);
            return;
          }
        }
        // On other /health failures (network blip, 5xx) proceed optimistically;
        // the response interceptor handles any real 401s per request.
        store.commit("isAdmin", true);
        await this.prepareAxios(msalApp);
        markAuthReady();
      } else {
        await this.handleRedirect(msalApp);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('Unauthorized');
      }
      console.error(error);
    }
  },

  // First visit or returning from the AAD redirect.
  async handleRedirect(msalApp) {
    try {
      const authRes = await msalApp.handleRedirectPromise();
      if (authRes?.account) {
        msalApp.setActiveAccount(authRes.account);
      }
      if (authRes?.idToken) {
        localStorage.setItem('auth', JSON.stringify(authRes));
        const requestCookies = await axios.post(`/api/set_cookie`, {
          id_token: authRes.idToken
        }, {
          withCredentials: true,
        });
        if (requestCookies.status === 200) {
          console.log('cookies set');
        }
      }
      const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0] || null;
      if (account) {
        msalApp.setActiveAccount(account);
        store.commit("msalApp", msalApp);
        // msalApp is often the same object reference committed earlier, so Vuex
        // won't re-trigger computeds reading it — track the account explicitly.
        store.commit("activeAccount", account);
        store.commit("isAdmin", true);
        if (!authRes?.idToken) {
          // Cached account but no fresh redirect result — make sure the
          // backend cookie exists before the app starts calling /api.
          await this.refreshSession();
        }
        await this.prepareAxios(msalApp);
        markAuthReady();
        // Coming back from the AAD redirect we always land on "/" — return to
        // the deep link (e.g. /approval-requests from the email) if one was saved.
        const savedRoute = sessionStorage.getItem(POST_LOGIN_ROUTE_KEY);
        if (savedRoute) {
          sessionStorage.removeItem(POST_LOGIN_ROUTE_KEY);
          if (savedRoute !== location.pathname + location.search) {
            location.replace(savedRoute);
          }
        }
      } else {
        await this.login(msalApp);
      }
    } catch (error) {
      console.error(error);
      await this.login(msalApp);
    }
  },

  async login(msalApp) {
    if (loginInProgress) return;
    loginInProgress = true;
    console.log('Logging in...');
    // Remember where the user was heading (e.g. /approval-requests from the
    // notification email) so handleRedirect can return them there after sign-in.
    try {
      const route = location.pathname + location.search;
      if (route && route !== '/') {
        sessionStorage.setItem(POST_LOGIN_ROUTE_KEY, route);
      }
    } catch (e) { /* storage unavailable — deep link just falls back to home */ }
    const app = msalApp || store.state.msalApp;
    const logindata = store.state.logindata;
    await app.loginRedirect({
      scopes: ['openid', 'profile', `https://${logindata.DATAVERSE_DOMAIN}/.default`],
      redirectUri: location.origin + "/",
    });
  },

  // Silently re-acquires a fresh id token from MSAL and re-sets the backend
  // session cookie. Returns true on success, false when interactive sign-in
  // is required.
  async refreshSession() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const msalApp = store.state.msalApp;
        const logindata = store.state.logindata;
        if (!msalApp || !logindata) return false;
        const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0];
        if (!account) return false;
        msalApp.setActiveAccount(account);
        // forceRefresh: the cached idToken can be as stale as the cookie.
        const result = await msalApp.acquireTokenSilent({
          scopes: ['openid', 'profile', `https://${logindata.DATAVERSE_DOMAIN}/.default`],
          forceRefresh: true,
        });
        if (!result?.idToken) return false;
        await axios.post(`/api/set_cookie`, {
          id_token: result.idToken
        }, {
          withCredentials: true,
        });
        return true;
      } catch (error) {
        console.error('Silent session refresh failed', error);
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  },

  async prepareAxios() {
    const accessToken = await this.getAccessToken();

    const instance = axios.create({
      baseURL: `${location.origin}/api`,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true,
    });
    installAuthGuard(instance);
    // Many components call the global axios directly with '/api/...' URLs —
    // guard it too, but only once.
    if (!globalGuardInstalled) {
      installAuthGuard(axios);
      globalGuardInstalled = true;
    }
    store.commit('api', instance);
  },

  async getAccessToken() {
    const msalApp = store.state.msalApp;
    const logindata = store.state.logindata;
    const accounts = msalApp.getAllAccounts();
    if (accounts.length > 0) {
      msalApp.setActiveAccount(accounts[0]);
      const result = await msalApp.acquireTokenSilent({
        scopes: [`https://${logindata.DATAVERSE_DOMAIN}/.default`],
      });
      return result.accessToken;
    } else {
      throw new Error('No accounts found.');
    }
  },

  async removeAccount() {
    try {
      const msalApp = store.state.msalApp;
      const account = msalApp.getActiveAccount();

      if (account) {
        msalApp.setActiveAccount(null);
        sessionStorage.clear();
        localStorage.removeItem('auth');
        store.commit("msalApp", null);
        store.commit("activeAccount", null);
        store.commit("logindata", null);

        await msalApp.logoutRedirect({
          postLogoutRedirectUri: location.origin,
        });

      } else {
        throw new Error('No accounts found.');
      }
    } catch (error) {
      console.error('Unable to logout', error);
    }
  },

  displayname: computed(() => {
    const account = store.state.activeAccount;
    return account ? account.name : null;
  }),

  username: computed(() => {
    const account = store.state.activeAccount;
    return account ? account.username : null;
  }),
};

export default authModule;
